import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { avatarColor } from '../../utils/companyHelpers';

function currencySymbol(code) {
  if (code === 'USD') return '$';
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return `${code} `;
}

function formatSalaryBrowse(min, max, currency, period) {
  const sym = currencySymbol(currency);
  const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const a = nf.format(Number(min));
  const b = nf.format(Number(max));
  const periodPart =
    {
      annual: '/ year',
      monthly: '/ month',
      hourly: '/ hour',
      yearly: '/ year',
      project: '',
    }[period] || '/ month';
  return `${sym}${a}–${sym}${b} ${periodPart}`.trim();
}

function timeAgo(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString);
  const now = new Date();
  const diffMs = now - then;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Today';
  if (days === 1) return '1 day ago';
  if (days >= 2 && days <= 6) return `${days} days ago`;
  if (days >= 7 && days <= 13) return '1 week ago';
  const weeks = Math.floor(days / 7);
  if (days >= 14 && days <= 29) return `${weeks} weeks ago`;
  return '1 month ago';
}

function formatEmploymentLabel(value) {
  if (!value) return '';
  return String(value)
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

function formatSeniorityTags(levels) {
  if (!levels || !Array.isArray(levels) || levels.length === 0) return null;
  const labels = {
    entry: 'Entry Level',
    mid: 'Mid-Level',
    senior: 'Senior',
    lead: 'Lead / Principal',
  };
  return levels.map((l) => labels[l] || l).join(' / ');
}

export default function JobCard({ job, showCompany = true }) {
  const featured = Boolean(job.is_featured);
  const company = job.company || {};
  const categoryName = job.category?.name || job.category_name || '';
  const logoUrl = company.logo_url || company.logo_path || null;
  const initial = (company.name && company.name.trim()[0]) || '?';
  const salaryText = formatSalaryBrowse(
    job.salary_min,
    job.salary_max,
    job.salary_currency,
    job.salary_period
  );
  const cardClass = `rwm-job-card border rounded p-3 mb-3 ${featured ? 'rwm-job-card--featured' : ''}`;

  return (
    <div
      className={cardClass}
      style={{ borderColor: 'var(--rwm-border)' }}
      data-testid="rwm-job-card"
    >
      <Row className="align-items-start g-3">
        {showCompany ? (
          <Col xs="auto">
            <div style={{ width: 48, height: 48 }}>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={company.name || 'Company'}
                  width={48}
                  height={48}
                  className="rounded"
                  style={{ objectFit: 'cover', width: 48, height: 48 }}
                />
              ) : (
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold"
                  style={{
                    width: 48,
                    height: 48,
                    backgroundColor: avatarColor(company.name || ''),
                    fontSize: 18,
                  }}
                >
                  {initial.toUpperCase()}
                </div>
              )}
            </div>
          </Col>
        ) : null}
        <Col>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
            <div>
              <Link
                to={`/jobs/${job.slug}`}
                className="rwm-browse-jobs__title-link fw-semibold text-decoration-none h5 mb-1 d-inline-block"
              >
                {job.title}
              </Link>
              {showCompany ? (
                <div className="small" style={{ color: 'var(--rwm-slate)' }}>
                  {company.name}
                  {categoryName ? ` · ${categoryName}` : ''}
                </div>
              ) : categoryName ? (
                <div className="small" style={{ color: 'var(--rwm-slate)' }}>
                  {categoryName}
                </div>
              ) : null}
            </div>
            <div className="d-flex flex-column align-items-end gap-1">
              {featured ? (
                <span
                  className="badge rounded-pill rwm-browse-jobs__featured-badge"
                  data-testid="rwm-featured-badge"
                >
                  Featured
                </span>
              ) : null}
              <span className="badge rounded-pill rwm-salary-badge" data-testid="rwm-salary-badge">
                {salaryText}
              </span>
              <span className="small" style={{ color: 'var(--rwm-slate)' }}>
                {timeAgo(job.approved_at)}
              </span>
            </div>
          </div>
          <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
            {job.employment_type ? (
              <span
                className="badge rounded-pill rwm-browse-jobs__employment-badge"
                data-testid="rwm-employment-tag"
              >
                {formatEmploymentLabel(job.employment_type)}
              </span>
            ) : null}
            <span
              className="badge rounded-pill rwm-browse-jobs__employment-badge"
              data-testid="rwm-location-tag"
            >
              {job.location_country
                ? job.location_country
                : job.location_eligibility != null && job.location_eligibility !== ''
                  ? job.location_eligibility
                  : ''}
            </span>
            {formatSeniorityTags(job.seniority_level) ? (
              <span
                className="badge rounded-pill rwm-browse-jobs__employment-badge"
                data-testid="rwm-seniority-tag"
              >
                {formatSeniorityTags(job.seniority_level)}
              </span>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              as={Link}
              to={`/jobs/${job.slug}`}
              data-testid="rwm-job-apply"
            >
              Apply Now
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}
